import React, { useMemo } from "react";
import { useCallback } from "react";
import { useSelectedDayItems } from "@/hooks/useSelectedDayItems";
import useTripStore from "@/stores/tripBuilder/useTrip";
import { format } from "date-fns";
import { ItineraryItem, Location } from "@/types";
import DayItem from "@tripbuilder/cat-overview/right/DayItem";
import { useLocationData } from "@/hooks/useLocations";
import useTransportStore from "@/stores/tripBuilder/useTransport";
import { DateTime } from "luxon";
import logger from "@/utils/logger";

interface TravelDayItem extends ItineraryItem {
  type: "travel-day";
}

interface DayViewComponentProps {
  day: number;
  date: string;
  switchToSchedule: () => void;
  handleTransportButtonClick: (location: Location) => void;
}

const DayViewComponent: React.FC<DayViewComponentProps> = ({
  day,
  date,
  switchToSchedule,
  handleTransportButtonClick,
}) => {
  const { selectedLocation, setSelectedDay, tripData } = useTripStore();
  const { previousLocation, nextLocation, isLastLocation, isFirstLocation } =
    useLocationData();
  const getEffectiveArrivalDate = useTransportStore(
    (state) => state.getEffectiveArrivalDate
  );

  const handleDayClick = useCallback(() => {
    setSelectedDay(date);
    switchToSchedule();
  }, [date, setSelectedDay, switchToSchedule]);

  const selectedDayItems = useSelectedDayItems(date, selectedLocation?.id);
  const dayDate = format(date, "do MMM");

  logger.log("selectedDayItems for day", date, selectedDayItems);

  const effectiveArrivalDate = useMemo(() => {
    if (!tripData || !selectedLocation) return null;
    const date = getEffectiveArrivalDate(
      tripData?.transport,
      selectedLocation?.id
    );
    if (!date) {
      return DateTime.fromISO(selectedLocation?.date.start_date)
        .setZone(selectedLocation?.date.time_zone)
        .set({ hour: 0, minute: 0 })
        .toISO();
    }
    return date;
  }, [tripData, selectedLocation, getEffectiveArrivalDate]);

  const filteredSelectedDayItems = useMemo(() => {
    return selectedDayItems.filter((item) => {
      if (item.name.startsWith("Staying at:")) {
        return false;
      }
      return true;
    });
  }, [selectedDayItems]);

  const dayNumber = useMemo(() => {
    if (!effectiveArrivalDate) {
      return `Day ${day}`;
    }

    const currentDate = DateTime.fromISO(date)
      .setZone(selectedLocation?.date.time_zone, { keepLocalTime: true })
      .startOf("day");

    const arrivalDate = DateTime.fromISO(effectiveArrivalDate).setZone(
      selectedLocation?.date.time_zone
    );

    if (currentDate.hasSame(arrivalDate, "day")) {
      return "Arrival Day";
    }

    const daysDiff = currentDate.diff(arrivalDate, "days").days;
    const roundedDaysDiff = Math.ceil(daysDiff);

    if (roundedDaysDiff <= 0) {
      return "Travel Day";
    } else {
      return `Day ${roundedDaysDiff}`;
    }
  }, [date, effectiveArrivalDate, day, selectedLocation?.date.time_zone]);

  return (
    <div className="day-template w-full mb-3">
      <div className="w-full flex flex-row items-center mb-4 transition ease-in-out duration-300 space-x-4 group pt-1 pr-2">
        <button
          onClick={handleDayClick}
          className="flex flex-row min-w-[90px] day-button py-1.5 bg-indigo-100/75 rounded-md hover:bg-indigo-200/75 transition duration-300 ease-in-out items-center justify-center"
        >
          <h3 className="w-full day-number text-[14px] font-semibold tracking-tight text-indigo-800 text-center">
            {dayDate}
          </h3>
        </button>
        <div className="h-[0.6px] rounded-full bg-neutral-300 group-hover:bg-neutral-600 w-[93%] transition ease-in-out duration-300" />
        <h3 className="text-neutral-300 whitespace-nowrap day-date text-sm font-normal tracking-tight group-hover:text-neutral-700 transition ease-in-out duration-300 py-2 rounded-lg">
          {dayNumber}
        </h3>
      </div>
      <div className="day-container flex flex-col items-center justify-start space-y-4">
        {filteredSelectedDayItems.length === 0 ? (
          <div className="empty-day-content w-full">
            <div className="emptyDayContent py-8 w-full h-full flex flex-col items-center justify-center bg-neutral-100/75 rounded-lg">
              <h1 className="text-lg font-semibold text-neutral-600"></h1>
              <p className="mt-2 text-[13px] font-light text-neutral-500">
                Add activities to your day to see them here.
              </p>
              <button
                onClick={handleDayClick}
                className="addActivityButton flex items-center justify-center w-5 h-5 bg-neutral-900 rounded-full mt-4 transition ease-in-out duration-300"
              >
                {/* svg plus */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
              </button>
            </div>
          </div>
        ) : (
          filteredSelectedDayItems.map((activity, index) => (
            <DayItem
              key={`${activity.name}-${index}`}
              selectedDay={date}
              activity={activity}
              index={index}
              locationTimeZone={selectedLocation?.date.time_zone || "UTC"}
              handleTransportButtonClick={handleTransportButtonClick}
              previousLocation={previousLocation}
              nextLocation={nextLocation}
              selectedLocation={selectedLocation}
              effectiveArrivalDate={effectiveArrivalDate}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default React.memo(DayViewComponent);
